import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faTrash, faUpload } from "@fortawesome/free-solid-svg-icons";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Loading from "components/Loading";

import localLawService from "api/localLawService";
import { ENotificationType, notification } from "utils/notification";
import { uploadPublicFile } from "utils/uploadPublicFile";

import styles from "./styles.module.scss";

interface Props {
  actId: string;
  onClose: () => void;
  onSaved: () => void;
}

// Rozstrzygnięcie nadzorcze wojewody — odwzorowanie okna LegalActRepeal z Magistratu
// oraz sekcji „Rozstrzygnięcia nadzorcze" metryki (BinaryAttachmentType.SupervisoryDecision).
//
// Skan rozstrzygnięcia jest plikiem JAWNYM — trafia do repozytorium bez szyfrowania,
// tak jak reszta akt aktu prawa miejscowego.

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const toDateInput = (iso: string | null | undefined) =>
  iso ? new Date(iso).toISOString().slice(0, 10) : "";

const SupervisoryRulingModal = ({ actId, onClose, onSaved }: Props) => {
  const actQuery = localLawService.useGetAct(actId);
  const { mutate: save, isLoading: saving } = localLawService.useSaveSupervisoryRuling();

  const fileInput = useRef<HTMLInputElement>(null);

  const [repeal, setRepeal] = useState(false);
  const [partialRepeal, setPartialRepeal] = useState(false);
  const [info, setInfo] = useState("");
  const [date, setDate] = useState("");
  const [fileUri, setFileUri] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const act = actQuery.data?.data;

  useEffect(() => {
    if (!act) return;

    setRepeal(act.Repeal);
    setPartialRepeal(act.PartialRepeal);
    setInfo(act.RepealInfo ?? "");
    setDate(toDateInput(act.SupervisoryRulingDate));
    setFileUri(act.SupervisoryRulingUri);
    setFileName(act.SupervisoryRulingFileName);
  }, [act]);

  const pickFile = async (file: File) => {
    setUploading(true);

    try {
      const uploaded = await uploadPublicFile(file);

      setFileUri(uploaded.Uri);
      setFileName(uploaded.FileName);
    } catch (e) {
      notification({
        type: ENotificationType.ERROR,
        title: "Błąd",
        text: errorText(e, "Nie udało się wgrać skanu rozstrzygnięcia."),
      });
    } finally {
      setUploading(false);
    }
  };

  const submit = () => {
    save(
      {
        id: actId,
        request: {
          Repeal: repeal,
          PartialRepeal: partialRepeal,
          RepealInfo: info.trim() || null,
          SupervisoryRulingDate: date || null,
          FileUri: fileUri,
          FileName: fileName,
        },
      },
      {
        onSuccess: onSaved,
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: errorText(e, "Nie udało się zapisać rozstrzygnięcia."),
          }),
      },
    );
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>Rozstrzygnięcie nadzorcze</div>

        <div className={styles.modalBody}>
          {actQuery.isLoading ? (
            <Loading />
          ) : (
            <>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={repeal}
                  onChange={(e) => {
                    setRepeal(e.target.checked);
                    if (e.target.checked) setPartialRepeal(false);
                  }}
                />
                Stwierdzono nieważność aktu w całości
              </label>

              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={partialRepeal}
                  onChange={(e) => {
                    setPartialRepeal(e.target.checked);
                    if (e.target.checked) setRepeal(false);
                  }}
                />
                Stwierdzono nieważność w części
              </label>

              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  Zakres rozstrzygnięcia / uzasadnienie
                </label>
                <textarea
                  className={styles.textArea}
                  value={info}
                  onChange={(e) => setInfo(e.target.value)}
                />
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Data rozstrzygnięcia</label>
                  <input
                    className={styles.textInput}
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
              </div>

              <div className={styles.sectionTitle}>Skan rozstrzygnięcia</div>

              {fileUri ? (
                <div className={styles.attachmentRow}>
                  <FontAwesomeIcon icon={faFilePdf} />
                  <span style={{ flex: 1 }}>{fileName ?? "rozstrzygnięcie.pdf"}</span>
                  <button
                    type="button"
                    className={`${styles.toolBtn} ${styles.toolBtnDanger}`}
                    title="Odepnij skan"
                    onClick={() => {
                      setFileUri(null);
                      setFileName(null);
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ) : (
                <>
                  <HollowButton
                    text={uploading ? "Wysyłanie..." : "Wgraj skan (PDF)"}
                    element={
                      <span>
                        <FontAwesomeIcon icon={faUpload} />{" "}
                        {uploading ? "Wysyłanie..." : "Wgraj skan (PDF)"}
                      </span>
                    }
                    onClick={() => !uploading && fileInput.current?.click()}
                  />
                  <input
                    ref={fileInput}
                    type="file"
                    accept="application/pdf,.pdf"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (file) pickFile(file);
                    }}
                  />
                </>
              )}

              <span className={styles.hint}>
                Stwierdzenie nieważności w całości ustawia status metrykalny aktu na
                „Unieważniony".
              </span>
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <HollowButton text="Anuluj" onClick={onClose} />
          <Button text="Zapisz" loading={saving} disabled={uploading} onClick={submit} />
        </div>
      </div>
    </div>
  );
};

export default SupervisoryRulingModal;
